import { ChangeDetectionStrategy, Component, Inject, ViewChild } from "@angular/core";
import { RoleConnection } from "@common/ADAPT.Common.Model/organisation/role-connection";
import { IBreezeEntity } from "@common/lib/data/breeze-entity.interface";
import { BreezeEntityUtilities } from "@common/lib/data/breeze-entity-utilities";
import { IBreezeModel } from "@common/lib/data/breeze-model.interface";
import { CommonDataService } from "@common/lib/data/common-data.service";
import { ADAPT_DIALOG_DATA } from "@common/ux/adapt-common-dialog/adapt-common-dialog.globals";
import { BaseDialogComponent, DialogResolveData } from "@common/ux/adapt-common-dialog/base-dialog.component/base-dialog.component";
import { EditRoleConnectionComponent } from "app/features/architecture/integrated-architecture/edit-role-connection/edit-role-connection.component";
import { EntityState } from "breeze-client";
import { lastValueFrom } from "rxjs";

export interface IEditRoleConnectionDialogData {
    roleConnection: RoleConnection;
    model: IBreezeModel;
    endRoleConnection?: boolean;
}

@Component({
    selector: "adapt-edit-role-connection-dialog",
    standalone: false,
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: "./edit-role-connection-dialog.component.html",
})
export class EditRoleConnectionDialogComponent extends BaseDialogComponent<IEditRoleConnectionDialogData, RoleConnection> {
    public readonly dialogName = "EditRoleConnectionDialog";

    public hasChanges = false;
    public restoring = false;

    private readonly originalRoleConnection: RoleConnection;
    private readonly originalEntityState: EntityState;

    @ViewChild(EditRoleConnectionComponent)
    public set editRoleConnectionComponent(editRoleConnection: EditRoleConnectionComponent | undefined) {
        // end the role connection automatically when dialog opens
        // used for "end role connection" menu option
        if (this.data.endRoleConnection && editRoleConnection) {
            setTimeout(() => editRoleConnection.endRoleConnection(400));
        }
    }

    public constructor(
        @Inject(ADAPT_DIALOG_DATA) public data: IEditRoleConnectionDialogData,
        private commonDataService: CommonDataService,
    ) {
        super(DialogResolveData.Required);

        this.originalRoleConnection = BreezeEntityUtilities.cloneEntity(this.data.roleConnection);
        this.originalEntityState = this.data.roleConnection.entityAspect.entityState;
    }

    public onRoleConnectionChanged() {
        this.hasChanges = true;
    }

    public async onRoleConnectionDeleted() {
        if (this.data.roleConnection.entityAspect.entityState.isAdded()) {
            this.data.roleConnection.entityAspect.rejectChanges();
        } else {
            await lastValueFrom(this.commonDataService.remove(this.data.roleConnection));
        }

        this.resolve(this.data.roleConnection);
    }

    public cancel() {
        // restore the role connection to how it was before you edited it in this dialog
        if (this.hasChanges) {
            // workaround for ExpressionChanged error
            this.restoring = true;
            this.restoreEntityFromClone(this.data.roleConnection, this.originalRoleConnection);
        }

        super.cancel();
    }

    private restoreEntityFromClone<T extends IBreezeEntity>(originalEntity: T, clonedEntity: T) {
        // if the entity was originally unchanged, just reject all changes.
        if (this.originalEntityState.isUnchanged()) {
            originalEntity.entityAspect.rejectChanges();
            return;
        }

        BreezeEntityUtilities.restoreEntityFromClone(originalEntity, clonedEntity);
    }
}
